import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { TrashIcon } from "../Icons";
import { PaginationComponent } from "@components/Pagination";

function TableWithPagination({
  columns,
  data,
  scrollableBody = false,
  colSize = 12,
  onRowClick,
  maxRowsPerPage = 10,
  currentPage,
  setCurrentPage,
  loading = false,
  totalPages,
  totalData,
  columnConfig = {},
  hasCheckbox = false,
  hasRadioButton = false,
  hasDeleteColumn = false, // Add prop to show Delete column
  onCheckboxChange,
  onDeleteRow, // Add prop to handle delete action
  showPagination = true,
  selectedRows = [],
  disabledRow = [],
  onItemsPerPageChange,
  rowKey = "seq",
  shouldShowGoToPage = colSize === 6 || colSize === 8 || colSize === 12,
  tableStyle = "default",
  containerStyle = {},
  rowHeight = null,
  stickyHeader = false,
  scrollY = null,
  isCheckboxDisabled = null, // Add prop to disable specific checkboxes
  isDeleteDisabled = null, // Add prop to disable specific delete buttons
  paddingYHeader = "py-4",
  /** Body cell vertical padding. Defaults to py-2 when rowHeight is set, else py-3. */
  paddingYCell = null,
  resizable = true, // Add prop to enable/disable column resizing
  showItemsPerPageSelector = false,
  onSort = null, // Callback for sorting: (columnKey, sortDirection) => void
  sortColumn = null, // Current sort column from parent
  sortDirection = null, // Current sort direction from parent: 'asc' | 'desc'
  sortable = false,
  useFixedSelect = true,
  dataTestId,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const cellPaddingY = paddingYCell ?? (rowHeight ? "py-2" : "py-3");

  const tableTestId = dataTestId || "table-with-pagination";
  const headerTestId = (suffix) => `${tableTestId}-header-${suffix}`;
  const noDataCellTestId = dataTestId ? `${dataTestId}-no-data-cell` : "no-data-cell";
  const tableData = Array.isArray(data) ? data : [];

  const [showSpinner, setShowSpinner] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(maxRowsPerPage);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [internalSortColumn, setInternalSortColumn] = useState(null);
  const [internalSortDirection, setInternalSortDirection] = useState(null);

  useEffect(() => {
    let showTimeout;
    let hideTimeout;

    if (loading) {
      showTimeout = setTimeout(() => setShowSpinner(true), 100);
    } else {
      hideTimeout = setTimeout(() => setShowSpinner(false), 100);
    }

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [loading]);


  const handleRowCheckboxChange = (row) => {
    if (!onCheckboxChange) return;

    if (typeof rowKey === "string") {
      const rowValue = row[rowKey];
      const isSelected = selectedRows.includes(rowValue);
      const newSelectedRows = isSelected
        ? selectedRows.filter((value) => value !== rowValue)
        : [...selectedRows, rowValue];
      onCheckboxChange(newSelectedRows);
    } else {
      const rowObj = rowKey.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {});
      const isSelected = selectedRows.some((selectedRow) =>
        Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]),
      );
      const newSelectedRows = isSelected
        ? selectedRows.filter((selectedRow) => !Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]))
        : [...selectedRows, rowObj];
      onCheckboxChange(newSelectedRows);
    }
  };

  const handleRowRadioButtonChange = (row) => {
    if (!onCheckboxChange) return;

    if (typeof rowKey === "string") {
      const value = row[rowKey];
      onCheckboxChange([value]);
    } else {
      onCheckboxChange([row]);
    }
  };

  const handleSelectAllChange = () => {
    if (!onCheckboxChange) return;

    // Filter out rows with disabled checkboxes
    const selectableRows = data.filter(row => !(isCheckboxDisabled && isCheckboxDisabled(row)));

    if (typeof rowKey === "string") {
      const allValues = selectableRows.map((row) => row[rowKey]);
      const allSelected = allValues.every((value) => selectedRows.includes(value));
      const newSelectedRows = allSelected
        // Unselect all rows on current page, keep selections from other pages
        ? selectedRows.filter((value) => !allValues.includes(value))
        // Select all rows on current page, keep existing selections
        : Array.from(new Set([...selectedRows, ...allValues]));
      onCheckboxChange(newSelectedRows);
    } else {
      const allRowObjects = selectableRows.map((row) => rowKey.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {}));
      const allSelected = allRowObjects.every((rowObj) =>
        selectedRows.some((selectedRow) => Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key])),
      );
      const newSelectedRows = allSelected
        // Unselect all rows on current page, keep selections from other pages
        ? selectedRows.filter(
          (selectedRow) =>
            !allRowObjects.some((rowObj) =>
              Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]),
            ),
        )
        // Select all rows on current page, keep existing selections
        : [
          ...selectedRows,
          ...allRowObjects.filter(
            (rowObj) =>
              !selectedRows.some((selectedRow) =>
                Object.keys(rowObj).every((key) => selectedRow[key] === rowObj[key]),
              ),
          ),
        ];
      onCheckboxChange(newSelectedRows);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    onItemsPerPageChange(value);
  };

  const currentSortColumn = sortColumn !== null ? sortColumn : internalSortColumn;
  const currentSortDirection = sortDirection !== null ? sortDirection : internalSortDirection;

  // Handle column header click for sorting
  const handleColumnSort = (columnKey, e) => {
    if (e.target.closest('.cursor-col-resize')) {
      return;
    }

    const config = columnConfig[columnKey] || {};
    if (config.sortable === false) {
      return;
    }

    let newDirection = 'asc';

    if (currentSortColumn === columnKey) {
      newDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';
    }

    if (sortColumn === null) {
      setInternalSortColumn(columnKey);
      setInternalSortDirection(newDirection);
    }

    if (onSort) {
      onSort(columnKey, newDirection);
    }
  };

  // Get sort icon for a column
  const getSortIcon = (columnKey) => {
    const isActive = currentSortColumn === columnKey;
    const dir = currentSortDirection;

    const getColor = (type) => {
      if (!isActive) return "#9EA5B3";
      return dir === type ? "#01BBFE" : "#9EA5B3";
    };
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <svg width="10" height="14" viewBox="0 0 16 28" fill="none">  <g transform="scale(2)">
          <path d="M8 5L5 1L2 5H8Z" fill={getColor("asc")} />
          <path d="M2 7L5 11L8 7H2Z" fill={getColor("desc")} />
        </g>
        </svg>
      </div>
    );
  };

  // Check if column is sortable
  const isColumnSortable = (columnKey) => {
    if (!sortable) return false;
    const config = columnConfig[columnKey] || {};
    return config.sortable !== false; // Default to true unless explicitly disabled
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.target.closest('th');
    if (!th) return;

    const actualWidth = th.offsetWidth;
    const table = th.closest('table');
    if (!table) return;

    // Find the scrollable container and check scrollbar state once at the start
    const scrollContainer = table.closest('.overflow-x-auto') || table.parentElement;
    const hasScrollbar = scrollContainer &&
      scrollContainer.scrollWidth > scrollContainer.clientWidth;

    const allThs = Array.from(table.querySelectorAll('thead th'));
    const allTds = Array.from(table.querySelectorAll(`tbody td:nth-child(${columnIndex + 1 + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0)})`));

    const dataColumnOffset = (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0);
    const dataColumnIndex = columnIndex + dataColumnOffset;

    const allColumnWidths = [];
    const allColumnThs = [];
    const allColumnTds = [];

    for (let i = 0; i < columns.length; i++) {
      const thIndex = i + dataColumnOffset;
      if (thIndex < allThs.length) {
        const th = allThs[thIndex];
        const width = columnWidths[i] || th.offsetWidth;
        allColumnWidths.push(width);
        allColumnThs.push(th);
        const colTds = Array.from(table.querySelectorAll(`tbody td:nth-child(${thIndex + 1})`));
        allColumnTds.push(colTds);
      }
    }

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    table.style.transition = 'none';
    table.classList.add('resizing');

    const resizeData = {
      index: columnIndex,
      startX: e.clientX,
      startWidth: actualWidth,
      th: th,
      table: table,
      scrollContainer: scrollContainer,
      hasScrollbar: hasScrollbar,
      nextTh: columnIndex < columns.length - 1 ? allThs[dataColumnIndex + 1] : null,
      allTds: allTds,
      nextTds: columnIndex < columns.length - 1
        ? Array.from(table.querySelectorAll(`tbody td:nth-child(${dataColumnIndex + 2})`))
        : [],
      nextColumnWidth: columnIndex < columns.length - 1
        ? allColumnWidths[columnIndex + 1]
        : null,
      allColumnWidths: allColumnWidths,
      allColumnThs: allColumnThs,
      allColumnTds: allColumnTds,
    };

    setResizingColumn(resizeData);
  };

  const handleResizeMove = (e) => {
    if (!resizingColumn) return;

    e.preventDefault();

    const diff = e.clientX - resizingColumn.startX;
    const isReducing = diff < 0;

    const newWidth = Math.max(50, resizingColumn.startWidth + diff);
    const widthDiff = newWidth - resizingColumn.startWidth;

    for (let i = 0; i < resizingColumn.allColumnThs.length; i++) {
      let widthToApply;

      if (i === resizingColumn.index) {
        // Current column being resized
        widthToApply = newWidth;
      } else if (i < resizingColumn.index) {
        // Previous columns - keep their widths unchanged
        widthToApply = resizingColumn.allColumnWidths[i];
      } else {
        // Following columns
        if (isReducing) {
          if (resizingColumn.hasScrollbar) {
            // When reducing with scrollbar: keep following columns' widths unchanged (they shift left, table shrinks)
            widthToApply = resizingColumn.allColumnWidths[i];
          } else {
            // When reducing without scrollbar: expand following columns to fill the space
            const followingColumnsCount = resizingColumn.allColumnThs.length - resizingColumn.index - 1;
            if (followingColumnsCount > 0) {
              // Distribute the space (negative widthDiff) among all following columns
              const spacePerColumn = Math.abs(widthDiff) / followingColumnsCount;
              widthToApply = resizingColumn.allColumnWidths[i] + spacePerColumn;
              widthToApply = Math.max(20, widthToApply); // Minimum width
            } else {
              widthToApply = resizingColumn.allColumnWidths[i];
            }
          }
        } else {
          // When expanding: keep following columns' widths unchanged (they shift right)
          widthToApply = resizingColumn.allColumnWidths[i];
        }
      }

      if (resizingColumn.allColumnThs[i]) {
        resizingColumn.allColumnThs[i].style.width = `${widthToApply}px`;
      }

      if (resizingColumn.allColumnTds[i]) {
        resizingColumn.allColumnTds[i].forEach(td => {
          td.style.width = `${widthToApply}px`;
        });
      }
    }
  };

  const handleResizeEnd = () => {
    if (!resizingColumn) return;

    // Restore text selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    // Restore transitions on table
    if (resizingColumn.table) {
      resizingColumn.table.style.transition = '';
      resizingColumn.table.classList.remove('resizing');
    }

    // Get final widths from DOM for all columns and update React state
    // This ensures all column widths are preserved, especially previous columns
    const finalWidths = {};
    for (let i = 0; i < resizingColumn.allColumnThs.length; i++) {
      if (resizingColumn.allColumnThs[i]) {
        finalWidths[i] = resizingColumn.allColumnThs[i].offsetWidth;
      }
    }

    setColumnWidths((prev) => {
      return {
        ...prev,
        ...finalWidths,
      };
    });

    setResizingColumn(null);
  };

  useEffect(() => {
    if (resizingColumn) {
      const handleMove = (e) => {
        requestAnimationFrame(() => handleResizeMove(e));
      };

      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleResizeEnd, { once: true });
      document.addEventListener('mouseleave', handleResizeEnd, { once: true });

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.removeEventListener('mouseleave', handleResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        if (resizingColumn?.table) {
          resizingColumn.table.style.transition = '';
          resizingColumn.table.classList.remove('resizing');
        }
      };
    }
  }, [resizingColumn]);

  const isRowSelected = (row) => {
    if (!selectedRows || selectedRows.length === 0) return false;
    if (!rowKey) return false;

    if (typeof rowKey === "string") {
      const rowValue = row[rowKey];
      if (selectedRows.length > 0 && typeof selectedRows[0] === "object") {
        return selectedRows.some((selectedRow) => selectedRow[rowKey] === rowValue);
      } else {
        return selectedRows.includes(rowValue);
      }
    }

    if (Array.isArray(rowKey)) {
      return selectedRows.some((selectedRow) => {
        if (typeof selectedRow === "object") {
          return rowKey.every((key) => selectedRow[key] === row[key]);
        }
        return false;
      });
    }

    return false;
  };

  const isRowDisabled = (row) => {
    if (!disabledRow) return false;

    const disabledRowsArray = Array.isArray(disabledRow) ? disabledRow : [disabledRow];

    return disabledRowsArray.some((disabled) => {
      if (Array.isArray(rowKey)) {
        return rowKey.every((key) => row[key] === disabled[key]);
      } else {
        return row[rowKey] === disabled[rowKey];
      }
    });
  };

  const getRowStripeColor = (index) => {
    return index % 2 === 0 ? colors.bgTertiary : colors.bgPrimary;
  };

  const getCellTruncateClasses = (config) => {
    const shouldTruncate = config.truncate !== false;
    return {
      td: shouldTruncate ? "overflow-hidden truncate" : "overflow-visible",
      inner: shouldTruncate ? "truncate" : "",
    };
  };

  // Calculate if all selectable rows are selected
  const areAllSelectableRowsSelected = (() => {
    const selectableRows = tableData.filter(row => !(isCheckboxDisabled && isCheckboxDisabled(row)));
    return selectableRows.length > 0 && selectableRows.every((row) => isRowSelected(row));
  })();

  if (scrollableBody) {
    return (
      <div
        style={containerStyle}
        className={`flex flex-col relative flex-1 min-h-0`}
        data-testid={tableTestId}
      >
        {/* Loading Overlay */}
        {showSpinner && (
          <div
            className={`bg-opacity-50 absolute inset-0 z-10 ${colors.bgPrimary}`}
            data-testid={`${tableTestId}-loading`}
          >
            <div className="p-4">
              {/* Skeleton Header */}
              <div className="mb-5">
                <div className="flex space-x-4">
                  {Array.from({ length: columns.length }).map((_, index) => (
                    <div key={index} className="flex-1">
                      <div className={`h-6 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skeleton Rows */}
              {Array.from({ length: Math.min(5, tableData.length || 5) }).map((_, rowIndex) => (
                <div key={rowIndex} className="mb-4">
                  <div className="flex space-x-4">
                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                      <div key={colIndex} className="flex-1">
                        <div className={`h-5 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          className={`flex-1 overflow-auto min-h-0 border rounded-lg ${colors.borderPrimary}`}
          data-testid={`${tableTestId}-table-wrapper`}
        >
          {/* Table */}
          <table
            className={`w-full ${tableStyle === "default" ? "table-fixed" : tableStyle}`}
            data-testid={`${tableTestId}-table`}
          >
            <thead
              className={`${colors.bgPrimary} ${stickyHeader ? "sticky top-0 z-20" : ""}`}
              data-testid={`${tableTestId}-table-head`}
            >
              <tr className='bg-inherit'>
                {hasCheckbox && (
                  <th
                    className={`w-16 px-4 ${paddingYHeader} text-left`}
                    title="Select All"
                    data-testid={headerTestId("checkbox")}
                  >
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={areAllSelectableRowsSelected}
                        onChange={handleSelectAllChange}
                      />
                      <div
                        className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${areAllSelectableRowsSelected
                          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                          }`}
                        onClick={handleSelectAllChange}
                      >
                        {areAllSelectableRowsSelected && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                )}
                {hasRadioButton && (
                  <th
                    className={`w-12 min-w-[48px] max-w-[48px] px-2 ${paddingYHeader}`}
                    title="Select"
                    data-testid={headerTestId("radio")}
                  ></th>
                )}
                {columns.map((column, index) => {
                  const columnKey = column;
                  const config = columnConfig[columnKey] || {};
                  const { headerRender, title, hideBorder } = config;
                  const columnTitle = title || column;
                  const sortable = isColumnSortable(columnKey);

                  return (
                    <th
                      key={index}
                      data-testid={headerTestId(columnKey)}
                      className={`${colors.textSubTitle} px-4 ${paddingYHeader} text-left text-xs font-semibold tracking-wider overflow-hidden uppercase relative ${sortable ? 'cursor-pointer select-none hover:opacity-80' : ''}`}
                      title={!headerRender ? columnTitle : undefined}
                      onClick={sortable ? (e) => handleColumnSort(columnKey, e) : undefined}
                      style={{
                        ...config.style,
                        whiteSpace: config.style?.whiteSpace || "normal",
                        textOverflow: "ellipsis",
                        width: columnWidths[index] ? `${columnWidths[index]}px` : config.style?.width || 'auto',
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {headerRender ? (
                          <div className="flex items-center w-full">
                            <div className="flex-1">
                              {headerRender(columnKey)}
                            </div>
                            {sortable && getSortIcon(columnKey)}
                          </div>
                        ) : (
                          <>
                            <div className="truncate py-1" title={columnTitle}>
                              {columnTitle}
                            </div>
                            {sortable && getSortIcon(columnKey)}
                          </>
                        )}
                      </div>
                      {resizable && (
                        <div
                          className={`absolute right-0 top-0 h-full w-1 cursor-col-resize`}
                          onMouseDown={(e) => handleResizeStart(e, index)}
                          style={{ userSelect: 'none' }}
                        >
                          {!hideBorder && (index < columns.length - 1 || (index === columns.length - 1 && hasDeleteColumn)) && (
                            <div
                              className={`h-full border-r ${colors.borderPrimary}`}
                              style={{
                                height: 'calc(100% - 16px)',
                                margin: '8px 0px',
                              }}
                            ></div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
                {hasDeleteColumn && (
                  <th
                    className={`${colors.textSubTitle} w-20 p-4 text-center text-xs font-semibold tracking-wider whitespace-nowrap uppercase`}
                    data-testid={headerTestId("action")}
                  >
                    {t("common.action")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className={colors.bgTableBody} data-testid={`${tableTestId}-table-body`}>
              {!loading && tableData.length === 0 && (
                <tr data-testid={`${tableTestId}-empty-row`}>
                  <td
                    className={`px-4 py-8 text-center ${colors.textMuted}`}
                    data-testid={noDataCellTestId}
                    colSpan={
                      columns.length + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0) + (hasDeleteColumn ? 1 : 0)
                    }
                  >
                    {t("common.no-data-to-display")}
                  </td>
                </tr>
              )}
              {tableData.length > 0 &&
                tableData.map((row, index) => {
                  return (
                    <tr
                      key={index}
                      className={`${getRowStripeColor(index)} ${isRowSelected(row) ? `!${colors.bgSelectedRow}` : ""} ${isRowDisabled(row) ? "cursor-not-allowed opacity-50" : ""} ${onRowClick && !isRowDisabled(row) ? `cursor-pointer transition-colors ${colors.hoverTableRow}` : ""}}`} onClick={onRowClick && !isRowDisabled(row) ? () => onRowClick(row, index) : undefined}
                    >
                      {hasCheckbox && (
                        <td className={`w-16 px-4 ${cellPaddingY} ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                          {!(isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))) && (
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={isRowSelected(row)}
                                  onChange={() => handleRowCheckboxChange(row)}
                                />
                                <div
                                  className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowSelected(row)
                                    ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                    : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                    }`}
                                  onClick={() => handleRowCheckboxChange(row)}
                                >
                                  {isRowSelected(row) && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      )}
                      {hasRadioButton && (
                        <td
                          className={`w-12 min-w-[48px] max-w-[48px] px-2 ${cellPaddingY} ${rowHeight ? `h-[${rowHeight}px]` : ""}`}
                        >
                          <div onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-center">
                              <input
                                type="radio"
                                className="sr-only"
                                checked={isRowSelected(row)}
                                disabled={isRowDisabled(row)}
                                onChange={() => handleRowRadioButtonChange(row)}
                              />
                              <div
                                className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                  ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                  : isRowSelected(row)
                                    ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                    : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                  }`}
                                onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                              >
                                {isRowSelected(row) && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className={`h-2 w-2 rounded-full ${colors.bgActivePrimary}`}></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      {columns.map((column, colIndex) => {
                        const columnKey = column;
                        const config = columnConfig[columnKey] || {};
                        const { customRender } = config;
                        const cellTruncateClasses = getCellTruncateClasses(config);

                        return (
                          <td
                            key={colIndex}
                            className={`px-4 ${cellPaddingY} text-sm ${colors.textPrimary} ${cellTruncateClasses.td}`}
                            title={
                              config.attributeTitle !== false &&
                                (typeof row[columnKey] === "string" || typeof row[columnKey] === "number")
                                ? row[columnKey]
                                : undefined
                            }
                            style={{
                              ...config.style,
                              whiteSpace: config.style?.whiteSpace || "nowrap",
                              textOverflow: config.truncate === false ? undefined : "ellipsis",
                              ...(rowHeight && { height: `${rowHeight}px` }),
                              width: columnWidths[colIndex] ? `${columnWidths[colIndex]}px` : config.style?.width || 'auto',
                            }}
                          >
                            {customRender ? (
                              <div className={cellTruncateClasses.inner}>{customRender(row[columnKey], row, columnKey, index)}</div>
                            ) : (
                              <div className={cellTruncateClasses.inner}>{row[columnKey]}</div>
                            )}
                          </td>
                        );
                      })}
                      {hasDeleteColumn && (
                        <td className={`flex items-center justify-center px-4 ${cellPaddingY}`}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                onDeleteRow(row);
                              }
                            }}
                            disabled={isDeleteDisabled?.(row)}
                            className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                              ? `${colors.textDisabled} cursor-not-allowed`
                              : `${colors.iconDelete} ${colors.hoverTextDanger}`
                              }`}
                            title={t("common.delete")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <PaginationComponent
          dataTestId={tableTestId}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          totalData={totalData}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          shouldShowGoToPage={shouldShowGoToPage}
          showPagination={showPagination}
          showItemsPerPageSelector={showItemsPerPageSelector}
          useFixedSelect={useFixedSelect}
          data={tableData}
        />
      </div>
    );
  } else {
    return (
      <div className="relative" data-testid={tableTestId}>
        <div style={containerStyle} className={`overflow-hidden rounded-lg border ${colors.borderPrimary}`}>
          <div
            className={`overflow-x-auto${scrollY ? " overflow-y-auto" : ""}`}
            style={scrollY ? { maxHeight: scrollY } : {}}
          >
            {/* Loading Overlay */}
            {showSpinner && (
              <div
                className={`bg-opacity-50 absolute inset-0 z-10 ${colors.bgPrimary}`}
                data-testid={`${tableTestId}-loading`}
              >
                <div className="p-4">
                  {/* Skeleton Header */}
                  <div className="mb-5">
                    <div className="flex space-x-4">
                      {Array.from({ length: columns.length }).map((_, index) => (
                        <div key={index} className="flex-1">
                          <div className={`h-6 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skeleton Rows */}
                  {Array.from({ length: Math.min(5, tableData.length || 5) }).map((_, rowIndex) => (
                    <div key={rowIndex} className="mb-4">
                      <div className="flex space-x-4">
                        {Array.from({ length: columns.length }).map((_, colIndex) => (
                          <div key={colIndex} className="flex-1">
                            <div className={`h-5 ${colors.bgSkeleton} rounded animate-pulse`}></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Table */}
            <table
              className={`w-full ${tableStyle === "default" ? "table-fixed" : tableStyle}`}
              data-testid={`${tableTestId}-table`}
            >
              <thead
                className={`${colors.bgPrimary} ${stickyHeader ? "sticky top-0 z-20" : ""}`}
                data-testid={`${tableTestId}-table-head`}
              >
                <tr className='bg-inherit'>
                  {hasCheckbox && (
                    <th
                      className={`w-16 px-4 ${paddingYHeader} text-left`}
                      title="Select All"
                      data-testid={headerTestId("checkbox")}
                    >
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={areAllSelectableRowsSelected}
                          onChange={handleSelectAllChange}
                        />
                        <div
                          className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${areAllSelectableRowsSelected
                            ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                            : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                            }`}
                          onClick={handleSelectAllChange}
                        >
                          {areAllSelectableRowsSelected && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                  )}
                  {hasRadioButton && (
                    <th
                      className={`w-12 min-w-[48px] max-w-[48px] px-2 ${paddingYHeader}`}
                      title="Select"
                      data-testid={headerTestId("radio")}
                    ></th>
                  )}
                  {columns.map((column, index) => {
                    const columnKey = column;
                    const config = columnConfig[columnKey] || {};
                    const { headerRender, title, hideBorder } = config;
                    const columnTitle = title || column;
                    const sortable = isColumnSortable(columnKey);

                    return (
                      <th
                        key={index}
                        data-testid={headerTestId(columnKey)}
                        className={`${colors.textSubTitle} px-4 ${paddingYHeader} text-left text-xs font-semibold tracking-wider overflow-hidden uppercase relative ${sortable ? 'cursor-pointer select-none hover:opacity-80' : ''}`}
                        title={!headerRender ? columnTitle : undefined}
                        onClick={sortable ? (e) => handleColumnSort(columnKey, e) : undefined}
                        style={{
                          ...config.style,
                          whiteSpace: config.style?.whiteSpace || "normal",
                          textOverflow: "ellipsis",
                          width: columnWidths[index] ? `${columnWidths[index]}px` : config.style?.width || 'auto',
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {headerRender ? (
                            <div className="flex items-center w-full">
                              <div className="flex-1">
                                {headerRender(columnKey)}
                              </div>
                              {sortable && getSortIcon(columnKey)}
                            </div>
                          ) : (
                            <>
                              <div className="truncate py-1" title={columnTitle}>
                                {columnTitle}
                              </div>
                              {sortable && getSortIcon(columnKey)}
                            </>
                          )}
                        </div>
                        {resizable && (
                          <div
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize`}
                            onMouseDown={(e) => handleResizeStart(e, index)}
                            style={{ userSelect: 'none' }}
                          >
                            {!hideBorder && (index < columns.length - 1 || (index === columns.length - 1 && hasDeleteColumn)) && (
                              <div
                                className={`h-full border-r ${colors.borderPrimary}`}
                                style={{
                                  height: 'calc(100% - 16px)',
                                  margin: '8px 0px',
                                }}
                              ></div>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                  {hasDeleteColumn && (
                    <th
                      className={`${colors.textSubTitle} w-25 p-4 text-center text-xs font-semibold tracking-wider whitespace-nowrap uppercase`}
                      data-testid={headerTestId("action")}
                    >
                      {t("common.action")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className={colors.bgTableBody} data-testid={`${tableTestId}-table-body`}>
                {!loading && tableData.length === 0 && (
                  <tr data-testid={`${tableTestId}-empty-row`}>
                    <td
                      className={`px-4 py-8 text-center ${colors.textMuted}`}
                      data-testid={noDataCellTestId}
                      colSpan={
                        columns.length + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0) + (hasDeleteColumn ? 1 : 0)
                      }
                    >
                      {t("common.no-data-to-display")}
                    </td>
                  </tr>
                )}
                {tableData.length > 0 &&
                  tableData.map((row, index) => {
                    return (
                      <tr
                        key={index}
                        className={`${getRowStripeColor(index)} ${isRowSelected(row) ? `!${colors.bgSelectedRow}` : ""} ${isRowDisabled(row) ? "cursor-not-allowed opacity-50" : ""} ${onRowClick && !isRowDisabled(row) ? `cursor-pointer transition-colors ${colors.hoverTableRow}` : ""}}`}
                        onClick={onRowClick && !isRowDisabled(row) ? () => onRowClick(row, index) : undefined}
                      >
                        {hasCheckbox && (
                          <td className={`w-16 px-4 ${cellPaddingY} ${rowHeight ? `h-[${rowHeight}px]` : ""}`}>
                            {!(isRowDisabled(row) || (isCheckboxDisabled && isCheckboxDisabled(row))) && (
                              <div onClick={(event) => event.stopPropagation()}>
                                <div className="flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={isRowSelected(row)}
                                    onChange={() => handleRowCheckboxChange(row)}
                                  />
                                  <div
                                    className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowSelected(row)
                                      ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                      : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                      }`}
                                    onClick={() => handleRowCheckboxChange(row)}
                                  >
                                    {isRowSelected(row) && (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        )}
                        {hasRadioButton && (
                          <td
                            className={`w-12 min-w-[48px] max-w-[48px] px-2 ${cellPaddingY} ${rowHeight ? `h-[${rowHeight}px]` : ""}`}
                          >
                            <div onClick={(event) => event.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                <input
                                  type="radio"
                                  className="sr-only"
                                  checked={isRowSelected(row)}
                                  disabled={isRowDisabled(row)}
                                  onChange={() => handleRowRadioButtonChange(row)}
                                />
                                <div
                                  className={`relative h-4 w-4 cursor-pointer rounded-full border-2 transition-all duration-200 ${isRowDisabled(row)
                                    ? `${colors.borderPrimary} ${colors.bgRadioInactive} cursor-not-allowed`
                                    : isRowSelected(row)
                                      ? `${colors.borderActiveDashed} ${colors.bgRadioSelected}`
                                      : `${colors.borderPrimary} ${colors.bgRadioInactive} ${colors.borderHoverPrimary}`
                                    }`}
                                  onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                                >
                                  {isRowSelected(row) && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className={`h-2 w-2 rounded-full ${colors.bgActivePrimary}`}></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          const columnKey = column;
                          const config = columnConfig[columnKey] || {};
                          const { customRender } = config;
                          const cellTruncateClasses = getCellTruncateClasses(config);

                          return (
                            <td
                              key={colIndex}
                              className={`px-4 ${cellPaddingY} text-sm ${colors.textPrimary} ${cellTruncateClasses.td}`}
                              title={
                                config.attributeTitle !== false &&
                                  ((typeof row[columnKey] === "string" || typeof row[columnKey] === "number") && columnKey !== "securities")
                                  ? row[columnKey]
                                  : undefined
                              }
                              style={{
                                ...config.style,
                                whiteSpace: config.style?.whiteSpace || "nowrap",
                                textOverflow: config.truncate === false ? undefined : "ellipsis",
                                ...(rowHeight && { height: `${rowHeight}px` }),
                                width: columnWidths[colIndex] ? `${columnWidths[colIndex]}px` : config.style?.width || 'auto',
                              }}
                            >
                              {customRender ? (
                                <div className={cellTruncateClasses.inner}>{customRender(row[columnKey], row, columnKey, index)}</div>
                              ) : (
                                <div className={cellTruncateClasses.inner}>{row[columnKey]}</div>
                              )}
                            </td>
                          );
                        })}
                        {hasDeleteColumn && (
                          <td className={`flex items-center justify-center px-4 ${cellPaddingY}`}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (onDeleteRow && !isDeleteDisabled?.(row)) {
                                  onDeleteRow(row);
                                }
                              }}
                              disabled={isDeleteDisabled?.(row)}
                              className={`rounded p-1 transition-colors ${isDeleteDisabled?.(row)
                                ? `${colors.textDisabled} cursor-not-allowed`
                                : `${colors.iconDelete} ${colors.hoverTextDanger}`
                                }`}
                              title={t("common.delete")}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination */}
        <PaginationComponent
          dataTestId={tableTestId}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          totalData={totalData}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          shouldShowGoToPage={shouldShowGoToPage}
          showPagination={showPagination}
          showItemsPerPageSelector={showItemsPerPageSelector}
          useFixedSelect={useFixedSelect}
          data={tableData}
        />
      </div>
    );
  }
}

export default TableWithPagination;
